import React, {FC, PropsWithChildren, useEffect, useRef} from 'react';
import {ConnectDragPreview, ConnectDragSource, DragSource, DragSourceSpec} from 'react-dnd';

import {globalDocument} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {fonts} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {roundedRect} from '../../../../helpers/canvasHelpers';
import {buildContactDisplayName} from '../../../../helpers/models/contactHelpers';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';

/*
 * Constants.
 */

export const RecipientsDragType = 'RECIPIENTS';

// Ancedotally this is the maximum width supported by Chrome/Safari for drag previews.
// https://transitory.technology/set-drag-image/
const previewMaxWidth = 400;
const previewNameLengthLimit = 40;
const previewFont = `14px ${fonts.system}`;
const previewPillMargin = 2;
const previewPillCornerRadius = 4;
const previewPillHeight = 22;
const previewPillPadding = 4;
const previewPillTextY = previewPillHeight - 6;

/*
 * Types.
 */

export interface RecipientsDragItem {
  recipients: ReadonlyArray<VisualRecipientModel>;
}

export interface RecipientsDropResult {
  droppedRecipientUids: ReadonlyArray<string>;
}

export interface RecipientsDragSourceProps {
  onDragBegin: RecipientsDragBeginHandler;
  onDragEnd: RecipientsDragEndHandler;
}

export type RecipientsDragBeginHandler = () => ReadonlyArray<VisualRecipientModel>;
export type RecipientsDragEndHandler = (droppedRecipientUids: ReadonlyArray<string>) => void;

interface RecipientsDragSourceCollectedProps {
  connectDragSource: ConnectDragSource;
  connectDragPreview: ConnectDragPreview;
}

interface RecipientsDragSourceComponentProps
  extends RecipientsDragSourceProps,
    RecipientsDragSourceCollectedProps {}

function isRecipientsDropResult(dropResult: any): dropResult is RecipientsDropResult {
  return dropResult && 'droppedRecipientUids' in dropResult && Array.isArray(dropResult.droppedRecipientUids);
}

/*
 * DnD.
 */

const spec: DragSourceSpec<RecipientsDragSourceProps, RecipientsDragItem> = {
  beginDrag(props) {
    return {
      recipients: props.onDragBegin(),
    };
  },
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }

    const result = monitor.getDropResult();
    if (!isRecipientsDropResult(result)) {
      return;
    }

    // Notify which recipients were dropped so they can be removed from the source.
    const {droppedRecipientUids} = result;
    props.onDragEnd(droppedRecipientUids);
  },
};

/*
 * Component.
 */

const RecipientsDragSourceComponent: FC<PropsWithChildren<RecipientsDragSourceComponentProps>> = (props) => {
  const {connectDragSource, connectDragPreview, onDragBegin} = props;
  const elementRef = useRef<HTMLDivElement>(null);
  const theme = useUserTheme();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    // TODO: Use new React DnD hooks.
    connectDragSource(element);

    // Override the draggable attribute to make the individual recipients draggable instead.
    // TODO: Try this cleaner approach: https://codesandbox.io/s/9j897k0mwy
    element.setAttribute('draggable', 'false');
  });

  // TODO: Use new React DnD DragLayer.
  async function onConnectDragPreviewAsync() {
    const recipients = onDragBegin();
    if (recipients.length === 0) {
      return;
    }

    let image;
    try {
      image = await getDragPreview(recipients, theme);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to draw drag preview`, e);
      return;
    }

    connectDragPreview(image);
  }

  return (
    // Render the drag preview after the mouse down event bubbles (which may have affected the user's selection) but
    // as soon as possible before the drag start (because rendering the preview is asynchronous).
    // eslint-disable-next-line
    <div ref={elementRef} onMouseDown={onConnectDragPreviewAsync}>
      {props.children}
    </div>
  );
};

export const RecipientsDragSource = DragSource(RecipientsDragType, spec, (connect) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
}))(RecipientsDragSourceComponent);

/*
 * Drag preview.
 */

function getRenderingContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get a rendering context');
  }

  ctx.font = previewFont;
  return ctx;
}

async function getDragPreview(
  recipients: ReadonlyArray<VisualRecipientModel>,
  theme: UserTheme,
): Promise<HTMLImageElement> {
  // Collect the recipient display names.
  const names = recipients.map((recipient) => {
    const name = buildContactDisplayName(recipient);
    return name.length > previewNameLengthLimit ? `${name.substr(0, previewNameLengthLimit - 1)}…` : name;
  });

  const canvas = globalDocument.createElement('canvas');
  canvas.width = previewMaxWidth;
  canvas.height = previewMaxWidth;

  // Layout the recipient pills so we can determine the true canvas size.
  let ctx = getRenderingContext(canvas);
  let cursorX = 0;
  let cursorY = 0;
  let maxX = 0;
  const metrics = names.map((name) => {
    const textMetrics = ctx.measureText(name);
    const textWidth = Math.round(textMetrics.width);
    const pillWidth = textWidth + 2 * previewPillPadding;

    // Wrap to the next line, if necessary.
    if (cursorX > 0 && cursorX + pillWidth > previewMaxWidth) {
      cursorX = 0;
      cursorY += previewPillHeight + previewPillMargin;
    }

    const pillX = cursorX;
    const pillY = cursorY;
    cursorX += pillWidth + previewPillMargin;
    maxX = Math.max(maxX, pillX + pillWidth);
    return {pillX, pillY, pillWidth};
  });

  // Reset the canvas size.
  canvas.width = maxX;
  canvas.height = cursorY + previewPillHeight;

  // Must get a new rendering context after resizing the canvas.
  ctx = getRenderingContext(canvas);

  // Draw the recipient pills.
  ctx.fillStyle = theme.palette.blue.shade40;
  ctx.beginPath();
  names.forEach((name, index) => {
    const {pillX, pillY, pillWidth} = metrics[index];
    roundedRect(ctx, pillX, pillY, pillWidth, previewPillHeight, previewPillCornerRadius);
  });
  ctx.fill();

  // Draw the recipient names.
  ctx.fillStyle = 'white';
  names.forEach((name, index) => {
    const {pillX, pillY} = metrics[index];
    ctx.fillText(name, pillX + previewPillPadding, pillY + previewPillTextY);
  });

  // Turn into an image, since not all browsers support a canvas as a drag preview.
  const url = canvas.toDataURL();
  const image = globalDocument.createElement('img');
  const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
    image.onload = () => {
      resolve(image);
    };
    image.onerror = () => {
      reject(new Error('Failed to load image from data URL'));
    };
  });
  image.src = url;
  return loadPromise;
}
